import { apiClient, ApiResponse } from "./client";

// ============================================================================
// Types - Lesson Details
// ============================================================================

export interface ComponentProgressInfo {
    status: 'not_started' | 'in_progress' | 'completed';
    // Video fields
    progressPercent?: number;
    lastPositionSec?: number;
    totalDurationSec?: number;
    // Quiz fields
    attemptCount?: number;
    bestScore?: number;
    latestScore?: number;
    passed?: boolean;
}

export interface ComponentDetail {
    type: 'video' | 'quiz' | 'assignment';
    location: 'pre' | 'main' | 'post' | 'wrapup' | 'checkup';
    enabled: boolean;
    required: boolean;
    progress: ComponentProgressInfo | null;
}

export interface LessonDetailsResponse {
    lessonId: number;
    title: string;
    bareTitle: string | null;
    thumbnailUrl: string | null;
    description: string | null;
    qid: string | null;                  // Queture ID (비디오 재생용)
    queturePoint: string | null;         // Queture 핵심 포인트 (queture_ext.point)
    components: ComponentDetail[];
    overallStatus: 'not_started' | 'in_progress' | 'completed';
    overallProgressPercent: number;
    sequenceOrder?: number;              // 레슨 순서 번호 (모듈 내)
}

// ============================================================================
// Types - Lesson Completion Summary
// ============================================================================

export interface OverallProgressInfo {
    completedLessons: number;
    totalLessons: number;
    completedModules: number;
    totalModules: number;
    progressPercent: number;
}

export interface NextLessonBasicInfo {
    lessonId: number;
    title: string;
    sequenceOrder?: number;
}

export interface ModuleCheckupQuizInfo {
    moduleId: number;
    moduleTitle: string;
}

export interface LessonCompletionSummaryResponse {
    lessonId: number;
    lessonTitle: string;
    sequenceOrder?: number;
    videoWatchedSeconds: number;
    quizCorrectCount: number;
    quizTotalCount: number;
    learningDurationSeconds: number;
    overallProgress?: OverallProgressInfo;
    nextLesson?: NextLessonBasicInfo;
    moduleCheckupQuiz?: ModuleCheckupQuizInfo;
}

// ============================================================================
// Types - Module Quiz Completion Summary
// ============================================================================

export interface ModuleQuizSessionResult {
    sessionId: number;
    componentProgressId: number;
    score: number;
    passed: boolean;
    correctCount: number;
    totalQuestions: number;
    completedAt: string;
}

export interface PreviousSessionResult {
    score: number;
    completedAt: string;
}

export interface NextLessonWithModuleInfo {
    lessonId: number;
    lessonTitle: string;
    moduleId: number;
    moduleTitle: string;
}

export interface ModuleQuizCompletionSummaryResponse {
    moduleId: number;
    moduleTitle: string;
    currentSession: ModuleQuizSessionResult;
    previousSession?: PreviousSessionResult | null;
    hadPreviouslyPassed: boolean;
    nextLesson?: NextLessonWithModuleInfo | null;
    isCourseCompleted: boolean;
}

// ============================================================================
// Types - Quiz Session Review (채점 결과 보기)
// ============================================================================

export interface QuizQuestionReview {
    quizId: string;
    questionNumber: number;
    questionText: string | null;
    userAnswer: unknown[] | null;
    correctAnswer: unknown[] | null;
    corrects: boolean[] | null;
    isCorrect: boolean;
    explanation?: QuizDescriptionResponse | null;
    varInstantiation?: Record<string, unknown> | null;
}

export interface QuizSessionReviewResponse {
    sessionId: number;
    totalQuestions: number;
    correctCount: number;
    score: number;
    passed: boolean;
    questions: QuizQuestionReview[];
}

// ============================================================================
// Types - Start Session
// ============================================================================

export type ComponentLocation = 'pre_quiz' | 'video' | 'post_quiz' | 'checkup_quiz' | 'wrapup_quiz' | 'assignment' | 'module_start_quiz' | 'module_end_quiz';

// ============================================================================
// Types - Diary Quiz (메타인지 다이어리)
// ============================================================================

export interface DiaryQuizItem {
    quizId: string;
    scriptId: string | null;
    type: 'multi_choice' | 'fill_blank' | 'select_text';
    title: string | null;
    problem: string | null;
}

export interface PreviousDiaryResponse {
    quizId: string;
    submittedResponse: unknown[] | null;
}

export interface DiaryQuizDataResponse {
    quizzes: DiaryQuizItem[];
    totalQuizCount: number;
    previousResponses?: PreviousDiaryResponse[] | null;
}

export interface DiaryQuizResponseItem {
    quizId: string;
    scriptId?: string | null;
    answers: unknown[];
    vars: Record<string, unknown>;
}

export interface DiaryQuizSubmitRequest {
    responses: DiaryQuizResponseItem[];
    classProductId?: number | null;  // B2B: Configuration 조회용 (레슨 완료 판단에 필요)
}

export interface DiaryQuizSubmitResponse {
    componentProgressId: number;
    status: 'completed';
    completedAt: string;
    totalResponses: number;
}

export interface StartSessionRequest {
    lessonId: number;
    componentType: 'video' | 'quiz' | 'assignment';
    componentLocation: ComponentLocation;
    classProductId?: number | null;
}

export type ModuleActivityType = 'start' | 'end'

export interface StartModuleActivityRequest {
    moduleId: number;
    activityType: ModuleActivityType;
    classProductId?: number | null;
    questionCount?: number;
    shuffleQuestions?: boolean;
}

export interface VideoDataResponse {
    bundleId: string | null;
    thumbnailUrl: string | null;
    // 본영상 (items.main)
    videoUrl: string | null;
    videoUrlMobile?: string | null;
    duration: number | null;
    // 짧은 버전 (items.summary)
    summaryUrl?: string | null;
    summaryDuration?: number | null;
    // 메타데이터
    drm: boolean;
    lastPositionSec: number;
    progressPercent: number;
    skipAllowed: boolean;
}

export interface QuizItemResponse {
    quizId: string;
    scriptId: string | null;
    type: string;
    title: string | null;
    problem: string | null;
}

/**
 * Previous Quiz Response Data - 이전 퀴즈 응답 데이터 (세션 재진입 시 제공)
 * 
 * 즉시 정오답 노출 설정 시, 이전에 푼 문제로 이동할 때 해설도 함께 제공
 */
export interface PreviousQuizResponseData {
    quizId: string;
    isCorrect: boolean;
    corrects?: boolean[] | null;
    correctAnswers?: unknown[] | null;
    submittedResponse?: unknown[] | null;
    varInstantiation?: Record<string, unknown> | null;
    description?: QuizDescriptionResponse | null;  // 해설 (즉시 정오답 노출 시)
}

export interface QuizDataResponse {
    quizzes: QuizItemResponse[];
    totalQuizCount: number;
    trialQuizCount: number;
    passingScore: number;
    // 재진입 시 이전 진행 상태 복원 (LEARNING_PROGRESS_API_PLAN.md #4.3)
    resumeFromIndex?: number | null;     // 이어서 풀기 시작할 인덱스 (null = 처음부터)
    answeredQuizIds?: string[] | null;   // 이미 응답한 퀴즈 ID 목록
    previousResponses?: PreviousQuizResponseData[] | null;  // 이전 응답 데이터 (재진입 시)
}

export interface StartSessionResponse {
    sessionId: number | null;
    componentProgressId: number;
    isNewSession: boolean;
    attemptNumber: number;
    status: string;
    videoData?: VideoDataResponse;
    quizData?: QuizDataResponse;
    diaryQuizData?: DiaryQuizDataResponse;
}

export interface ModuleActivityStatusResponse {
    moduleId: number;
    startActivityEnabled: boolean;
    endActivityEnabled: boolean;
    startActivityCompleted: boolean;
    endActivityCompleted: boolean;
}

// ============================================================================
// Types - Video Progress
// ============================================================================

export interface VideoProgressRequest {
    currentPositionSec: number;
    watchedDurationSec?: number;
    totalDurationSec?: number;
    autoComplete?: boolean;
}

export interface VideoProgressResponse {
    componentProgressId: number;
    status: 'in_progress' | 'completed';
    progressPercent: number;
    lastPositionSec: number;
    totalWatchTimeSec: number;
    isCompleted: boolean;
    completedAt?: string;
}

// ============================================================================
// Types - Video Event (이벤트 기반 로그)
// ============================================================================

export type VideoEventType = 'play' | 'pause' | 'seek' | 'end';

/**
 * Video Event Request - 시청 패턴 분석용 이벤트 기록
 *
 * 이벤트 발생 시에만 호출:
 * - play: startPositionSec만 사용 (구간 시작)
 * - pause: startPositionSec + endPositionSec (구간 종료, 시청)
 * - seek: startPositionSec + endPositionSec + seekToSec (스킵)
 * - end: startPositionSec + endPositionSec (이탈/완료)
 */
export interface VideoEventRequest {
    eventType: VideoEventType;
    startPositionSec: number;        // 구간 시작 위치 (play 시점)
    endPositionSec?: number | null;  // 구간 종료 위치 (pause/seek/end 시점)
    seekToSec?: number | null;       // seek 이벤트 시 이동 목표 위치
    playbackSpeed?: number;
    totalDurationSec?: number;
    requiredWatchPercent?: number;   // 자동 완료 임계값 (Configuration에서 전달, 기본값: 90)
    classProductId?: number | null;  // B2B: Configuration 조회용 (레슨 완료 판단에 필요)
}

export interface VideoEventResponse {
    logId: number;
    videoSessionId: number;
    eventType: string;
    totalWatchTimeSec: number;
    lastPositionSec: number;
    progressPercent: number;
    isCompleted: boolean;
}

// ============================================================================
// Types - Video Heartbeat (위치 업데이트 전용)
// ============================================================================

/**
 * Video Heartbeat Request - 위치 업데이트 전용
 *
 * 5초 간격으로 호출하여 위치만 업데이트.
 * 시청 로그(video_clip_log)는 /video-event API로 별도 기록.
 */
export interface VideoHeartbeatRequest {
    currentPositionSec: number;
    watchedDurationSec?: number;
    totalDurationSec?: number;
    requiredWatchPercent?: number;   // 자동 완료 임계값 (Configuration에서 전달, 기본값: 90)
    classProductId?: number | null;  // B2B: Configuration 조회용 (레슨 완료 판단에 필요)
}

export interface VideoHeartbeatResponse {
    videoSessionId: number;
    totalWatchTimeSec: number;
    lastPositionSec: number;
    progressPercent: number;
    isCompleted: boolean;
}

// ============================================================================
// Types - Quiz Response
// ============================================================================

export interface QuizResponseRequest {
    quizId: string;  // String to preserve precision for large IDs (Snowflake IDs exceed JS Number.MAX_SAFE_INTEGER)
    scriptId?: string | null;
    answers: unknown[];
    vars: Record<string, unknown>;  // Required - send {} if empty
    timeSpentSec?: number;
}

/**
 * Quiz Content View - 퀴즈 이미지/비디오 상세 정보
 * @see backend QuizContentView
 */
export interface QuizContentView {
    contentId?: string | null;
    name?: string | null;
    size?: number | null;
    uri?: string | null;
    mimeType?: string | null;
    signedUrl?: string | null;
    cloudfrontUrl?: string | null;
    url?: string | null;
    hls?: string | null;
    drm?: boolean | null;
    scaleX?: number | null;
    scaleY?: number | null;
}

/**
 * Quiz Description Response - 퀴즈 해설 (image 상세 정보 포함)
 * @see backend QuizDescriptionResponse
 */
export interface QuizDescriptionResponse {
    text: string | null;
    image?: QuizContentView | null;
    imageMobile?: QuizContentView | null;
    videoUrl?: string | null;
    desmos?: string | null;
}

export interface QuizResponseResponse {
    responseId: string;
    quizId: string;  // String to preserve precision for large Snowflake IDs
    isCorrect: boolean;
    corrects?: boolean[];
    correctAnswers?: unknown[];
    description?: QuizDescriptionResponse;
    score?: number;
    partialCredit?: number;
    difficultyLevel?: number;  // 난이도 레벨 (1-5, 디버깅용)
}

// ============================================================================
// Types - Complete Session
// ============================================================================

export interface CompleteSessionRequest {
    componentType: 'video' | 'quiz' | 'assignment';
    forceComplete?: boolean;
    classProductId?: number | null;  // B2B: Configuration 조회용 (레슨 완료 판단에 필요)
    passScore?: number;  // 퀴즈 통과 점수 (0이면 무조건 완료, 양수면 해당 % 이상 필요)
}

export interface LessonProgressUpdate {
    lessonId: number;
    status: 'in_progress' | 'completed';
    progressPercent: number;
    completedComponents: number;
    totalComponents: number;
}

export interface CompleteSessionResponse {
    componentProgressId: number;
    status: 'completed';
    completedAt: string;
    // Video fields
    totalWatchTimeSec?: number;
    progressPercent?: number;
    // Quiz fields
    score?: number;
    passed?: boolean;
    correctCount?: number;
    totalQuestions?: number;
    // Parent level update
    lessonProgress?: LessonProgressUpdate;
}

// ============================================================================
// API Functions
// ============================================================================

export const learningSessionApi = {
    /**
     * 레슨 상세 조회 (컴포넌트 및 진행 상태 포함)
     */
    getLessonDetails: async (params: {
        lessonId: number;
        classProductId?: number | null;
        productId?: number | null;
        classId?: number | null;
    }): Promise<LessonDetailsResponse> => {
        const queryParams = new URLSearchParams();
        if (params.classProductId != null) {
            queryParams.append('classProductId', params.classProductId.toString());
        }
        if (params.productId != null) {
            queryParams.append('productId', params.productId.toString());
        }
        if (params.classId != null) {
            queryParams.append('classId', params.classId.toString());
        }

        const queryString = queryParams.toString();
        const url = `/learning/lessons/${params.lessonId}/details${queryString ? `?${queryString}` : ''}`;

        const response = await apiClient.get<ApiResponse<LessonDetailsResponse>>(url);
        if (!response.data.data) {
            throw new Error("Lesson details not found");
        }
        return response.data.data;
    },

    /**
     * 학습 세션 시작
     */
    startSession: async (request: StartSessionRequest): Promise<StartSessionResponse> => {
        const response = await apiClient.post<ApiResponse<StartSessionResponse>>(
            '/learning/sessions/start',
            request
        );
        if (!response.data.data) {
            throw new Error("Failed to start session");
        }
        return response.data.data;
    },

    /**
     * 모듈 활동 세션 시작
     */
    startModuleActivity: async (
        request: StartModuleActivityRequest
    ): Promise<StartSessionResponse> => {
        const response = await apiClient.post<ApiResponse<StartSessionResponse>>(
            '/learning/modules/activities/start',
            request
        );
        if (!response.data.data) {
            throw new Error("Failed to start module activity");
        }
        return response.data.data;
    },

    /**
     * 비디오 진행 상태 업데이트
     */
    updateVideoProgress: async (
        componentProgressId: number,
        request: VideoProgressRequest
    ): Promise<VideoProgressResponse> => {
        const response = await apiClient.put<ApiResponse<VideoProgressResponse>>(
            `/learning/sessions/${componentProgressId}/video-progress`,
            request
        );
        if (!response.data.data) {
            throw new Error("Failed to update video progress");
        }
        return response.data.data;
    },

    /**
     * 비디오 위치 업데이트 (Heartbeat)
     * 5초 간격으로 호출하여 위치만 업데이트
     * 시청 로그(video_clip_log)는 recordVideoEvent로 별도 기록
     */
    recordVideoHeartbeat: async (
        componentProgressId: number,
        request: VideoHeartbeatRequest
    ): Promise<VideoHeartbeatResponse> => {
        const response = await apiClient.post<ApiResponse<VideoHeartbeatResponse>>(
            `/learning/sessions/${componentProgressId}/video-heartbeat`,
            request
        );
        if (!response.data.data) {
            throw new Error("Failed to record video heartbeat");
        }
        return response.data.data;
    },

    /**
     * 비디오 이벤트 기록 (시청 패턴 분석용)
     * 이벤트 발생 시에만 호출: play, pause, seek, end
     */
    recordVideoEvent: async (
        componentProgressId: number,
        request: VideoEventRequest
    ): Promise<VideoEventResponse> => {
        const response = await apiClient.post<ApiResponse<VideoEventResponse>>(
            `/learning/sessions/${componentProgressId}/video-event`,
            request
        );
        if (!response.data.data) {
            throw new Error("Failed to record video event");
        }
        return response.data.data;
    },

    /**
     * 퀴즈 응답 제출
     */
    submitQuizResponse: async (
        sessionId: number,
        request: QuizResponseRequest
    ): Promise<QuizResponseResponse> => {
        const response = await apiClient.post<ApiResponse<QuizResponseResponse>>(
            `/learning/sessions/${sessionId}/quiz-response`,
            request
        );
        if (!response.data.data) {
            throw new Error("Failed to submit quiz response");
        }
        return response.data.data;
    },

    /**
     * 세션 완료
     */
    completeSession: async (
        componentProgressId: number,
        request: CompleteSessionRequest
    ): Promise<CompleteSessionResponse> => {
        const response = await apiClient.post<ApiResponse<CompleteSessionResponse>>(
            `/learning/sessions/${componentProgressId}/complete`,
            request
        );
        if (!response.data.data) {
            throw new Error("Failed to complete session");
        }
        return response.data.data;
    },

    /**
     * 완료된 레슨 ID 목록 조회
     * @param curriculumId 커리큘럼 ID
     * @param classProductId B2B 클래스 상품 ID (optional)
     */
    getCompletedLessonIds: async (curriculumId: number, classProductId?: number): Promise<number[]> => {
        const params = classProductId ? { classProductId } : undefined;
        const response = await apiClient.get<ApiResponse<number[]>>(
            `/learning/progress/curriculum/${curriculumId}/completed-lessons`,
            { params }
        );
        return response.data.data || [];
    },

    /**
     * 모듈 활동 완료 상태 조회
     */
    getModuleActivityStatuses: async (
        moduleIds: number[],
        classProductId?: number | null
    ): Promise<ModuleActivityStatusResponse[]> => {
        if (moduleIds.length === 0) return [];
        const params: Record<string, string> = { moduleIds: moduleIds.join(',') };
        if (classProductId != null) {
            params.classProductId = classProductId.toString();
        }
        const response = await apiClient.get<ApiResponse<ModuleActivityStatusResponse[]>>(
            `/learning/modules/activity-status`,
            { params }
        );
        return response.data.data || [];
    },

    /**
     * 다이어리 퀴즈 응답 일괄 제출 (메타인지 다이어리)
     * 모든 문제 답변 완료 후 한 번에 제출
     */
    submitDiaryQuizResponses: async (
        componentProgressId: number,
        request: DiaryQuizSubmitRequest
    ): Promise<DiaryQuizSubmitResponse> => {
        const response = await apiClient.post<ApiResponse<DiaryQuizSubmitResponse>>(
            `/learning/sessions/${componentProgressId}/diary-quiz-response`,
            request
        );
        if (!response.data.data) {
            throw new Error("Failed to submit diary quiz responses");
        }
        return response.data.data;
    },

    /**
     * 레슨 완료 결과 요약 조회
     * 레슨 완료 화면에 표시할 학습 결과 데이터를 백엔드에서 조회
     */
    getLessonCompletionSummary: async (
        lessonId: number,
        classProductId?: number | null
    ): Promise<LessonCompletionSummaryResponse> => {
        const params: Record<string, string> = {};
        if (classProductId != null) {
            params.classProductId = classProductId.toString();
        }
        const response = await apiClient.get<ApiResponse<LessonCompletionSummaryResponse>>(
            `/learning/lessons/${lessonId}/completion-summary`,
            { params }
        );
        if (!response.data.data) {
            throw new Error("Failed to get lesson completion summary");
        }
        return response.data.data;
    },

    /**
     * 모듈 마무리 퀴즈 완료 결과 요약 조회
     * 모듈 마무리 퀴즈 완료 화면에 표시할 데이터를 백엔드에서 조회
     */
    getModuleQuizCompletionSummary: async (
        moduleId: number,
        classProductId?: number | null
    ): Promise<ModuleQuizCompletionSummaryResponse> => {
        const params: Record<string, string> = {};
        if (classProductId != null) {
            params.classProductId = classProductId.toString();
        }
        const response = await apiClient.get<ApiResponse<ModuleQuizCompletionSummaryResponse>>(
            `/learning/modules/${moduleId}/quiz-completion-summary`,
            { params }
        );
        if (!response.data.data) {
            throw new Error("Failed to get module quiz completion summary");
        }
        return response.data.data;
    },

    /**
     * 퀴즈 세션 채점 결과 조회
     * 채점 결과 보기 모달에 표시할 데이터를 백엔드에서 조회
     */
    getQuizSessionReview: async (sessionId: number): Promise<QuizSessionReviewResponse> => {
        const response = await apiClient.get<ApiResponse<QuizSessionReviewResponse>>(
            `/learning/quiz-sessions/${sessionId}/review`
        );
        if (!response.data.data) {
            throw new Error("Failed to get quiz session review");
        }
        return response.data.data;
    },
};
